// What each role does in ProTrack, in plain words, for somebody who has just arrived.
//
// WHERE THESE STATEMENTS COME FROM. Nothing here is a wish. Each line was checked against the
// application: a project a PM creates is live at once (no approval), who counter-signs a contract
// (Admin), who gives the two invoice approvals (the PM and the Admin, both), who pays (Finance),
// who reviews bills (the PM or Admin, never Finance), who verifies identity checks (Admin or HR),
// and who confirms a closure (Admin). The one-line
// summaries match the ones in the "Who is told what" notification document.
//
// If a rule changes in the code, change the line here in the same commit -- a guide that is
// wrong is worse than none, because a new person believes it.

// The stages of one piece of work, from a person joining to a project closing.
export const JOURNEY = [
  {
    id: 'join',
    title: 'Join',
    who: ['Consultant', 'HR', 'Admin'],
    text: 'A new person registers or accepts an invitation. Their identity papers are checked (HR or the Admin), then the Admin activates the account.',
  },
  {
    id: 'project',
    title: 'Set up the project',
    who: ['Project Manager'],
    text: 'A Project Manager creates the project and its milestones. It is live as soon as it is created.',
  },
  {
    id: 'team',
    title: 'Team and contract',
    who: ['Project Manager', 'Consultant', 'Admin'],
    text: 'The PM offers milestones. The consultant selects the ones they accept, and the PM or Admin approves. A contract is issued, the consultant signs it and the Admin counter-signs.',
  },
  {
    id: 'deliver',
    title: 'Deliver',
    who: ['Consultant', 'Project Manager'],
    text: 'The consultant submits deliverables, with bills for any expenses. The PM reviews each one: approve it, or send it back for changes.',
  },
  {
    id: 'invoice',
    title: 'Invoice',
    who: ['Project Manager', 'Consultant', 'Admin'],
    text: 'When every deliverable of a milestone is approved, an invoice is raised. The consultant confirms and signs it. The PM and the Admin must both approve it, in either order.',
  },
  {
    id: 'pay',
    title: 'Pay',
    who: ['Finance'],
    text: 'Only after both approvals, Finance records the payment. The consultant is told.',
  },
  {
    id: 'close',
    title: 'Close',
    who: ['Project Manager', 'Admin'],
    text: 'When every milestone is approved, the PM requests closure and the Admin confirms it. The Admin then archives the project for long-term keeping.',
  },
]

// Who starts, who decides, what happens next.
export const APPROVALS = [
  { what: 'New person', starts: 'Registers, or is invited', decides: 'HR or Admin checks identity; Admin activates', result: 'Account is active' },
  { what: 'New project', starts: 'Project Manager creates it', decides: 'No approval needed', result: 'Live straight away' },
  { what: 'Milestone selection', starts: 'PM offers; consultant selects', decides: 'PM or Admin approves', result: 'A contract is issued' },
  { what: 'Contract', starts: 'Consultant signs', decides: 'Admin counter-signs', result: 'Contract is active' },
  { what: 'Deliverable', starts: 'Consultant submits', decides: 'PM (or Admin) reviews', result: 'Approved, or sent back' },
  { what: 'Bill (expense)', starts: 'Consultant attaches it', decides: 'PM (or Admin) reviews', result: 'Approved, or sent back' },
  { what: 'Invoice', starts: 'Raised for a finished milestone; consultant confirms and signs', decides: 'PM and Admin, both', result: 'Finance can pay' },
  { what: 'Project closure', starts: 'PM requests it', decides: 'Admin confirms', result: 'Closed, then archived by the Admin' },
]

// One entry per role. `id` is the exact role name the server sends.
export const ROLES = [
  {
    id: 'Consultant',
    label: 'Consultant',
    tagline: 'Does the work: joins, signs, delivers, claims and gets paid.',
    firstSteps: [
      { text: 'Finish your onboarding: profile, CV, identity check and consent.', href: '/onboarding/my', link: 'My Onboarding' },
      { text: 'Review the milestones offered to you and select the ones you accept.', href: '/consultant/projects', link: 'My Projects' },
      { text: 'Review and sign your contract when it is ready.', href: '/contracts', link: 'My Contracts' },
    ],
    youDo: [
      'Complete onboarding, and keep your bank details correct.',
      'Select the milestones you accept, then review and sign your contract.',
      'Submit a deliverable for each milestone, and resubmit it if it is sent back.',
      'Attach bills for expenses you want repaid.',
      'Confirm and sign your invoice when it is ready.',
      'Ask for leave in My Leave.',
    ],
    notYours: [
      'You see only your own projects, contracts, bills and payments.',
      'You do not approve invoices or make payments.',
      'If you are asked to lead a sub-project, you manage that one project as well (see "How people get extra powers").',
    ],
    screens: ['My Onboarding', 'My Projects', 'My Contracts', 'My Leave', 'My Team'],
  },
  {
    id: 'Project Manager',
    label: 'Project Manager (PM)',
    tagline: 'Runs the projects: team, milestones, deliverables, approvals.',
    firstSteps: [
      { text: 'Open your projects and check each milestone and who is on the team.', href: '/projects', link: 'Projects' },
      { text: 'Review any deliverables or bills waiting for you.', href: '/dashboard', link: 'Dashboard' },
      { text: 'Check the invoices that need your approval.', href: '/dashboard', link: 'Dashboard' },
    ],
    youDo: [
      'Create projects and add their milestones. A new project is live as soon as you create it.',
      'Offer milestones to consultants, and approve the ones they select.',
      'Review deliverables and bills: approve them or send them back.',
      'Raise the invoice when a milestone is finished, and give the PM approval. The Admin gives the other.',
      'Request closure when every milestone is approved.',
      'Work with the team in project chat and documents.',
    ],
    notYours: [
      'You do not pay invoices; Finance does.',
      'You see the projects you manage. A Master PM, or someone granted that power, sees all projects.',
    ],
    screens: ['Projects', 'Contracts', 'Budget Tracking', 'My Team', 'Consultants', 'Templates'],
  },
  {
    id: 'Finance',
    label: 'Finance',
    tagline: 'Pays: tracks budgets and pays approved invoices.',
    firstSteps: [
      { text: 'Open the Invoice Queue to see what is ready for payment.', href: '/finance/invoices', link: 'Invoice Queue' },
      { text: 'Check budgets against what has been spent.', href: '/finance/budget', link: 'Budget Tracking' },
      { text: 'Look at the financial reports.', href: '/finance/reports', link: 'Financial Reports' },
    ],
    youDo: [
      'Pay invoices once both the PM and the Admin have approved them, and record the payment.',
      'Track budgets and spending for your projects.',
      'See the bills on each invoice before paying it (View Invoice). The PM or Admin reviews them.',
      'Follow up invoices that stay unpaid; you are reminded daily about any unpaid for more than 7 days.',
    ],
    notYours: [
      'You do not approve invoices; the PM and the Admin do.',
      'You see and pay invoices for the projects whose team you are on. (If a project has no Finance person on its team, any Finance user can.)',
      'You do not create projects or sign contracts.',
    ],
    screens: ['Projects', 'Contracts', 'Invoice Queue', 'Financial Reports', 'Budget Tracking', 'My Team'],
  },
  {
    id: 'Admin',
    label: 'Admin',
    tagline: 'Runs the company: approves people, projects, contracts and invoices.',
    firstSteps: [
      { text: 'Check who is waiting to be verified or activated.', href: '/onboarding', link: 'Onboarding' },
      { text: 'Open your dashboard for projects, contracts and invoices waiting for you.', href: '/dashboard', link: 'Dashboard' },
      { text: 'Decide who gets any extra powers.', href: '/admin/people', link: 'People & Access' },
    ],
    youDo: [
      'Approve new people and activate consultants once their identity is verified.',
      'Counter-sign contracts so they become active.',
      'Give the second invoice approval, together with the PM. Either can go first.',
      'Confirm project closure, and archive closed projects.',
      'Handle disputes, complaints and access requests (the Legal menu).',
      'Manage people and access, restore deleted items (Recycle Bin), see every archived project and legal hold in one place (Archive & Retention), and work through feedback and issues.',
    ],
    notYours: [
      'An Admin holds every extra power automatically.',
      'Running the platform itself (File Manager, the Platform panel) is for a Super Admin.',
    ],
    screens: ['Projects', 'Onboarding', 'People & Access', 'Contracts', 'Invoice Queue', 'Recycle Bin', 'Archive & Retention', 'Feedback & Issues', 'Disputes', 'Legal'],
  },
  {
    id: 'HR',
    label: 'HR',
    tagline: 'Looks after people: identity checks, leave and performance.',
    firstSteps: [
      { text: 'Open the HR dashboard for people, onboarding and compliance.', href: '/hr', link: 'Dashboard' },
      { text: 'See who is waiting for an identity check.', href: '/onboarding', link: 'Onboarding' },
      { text: 'Look at leave requests and people operations.', href: '/hr/people-ops', link: 'People Ops' },
    ],
    youDo: [
      'See the people directory and the HR figures.',
      'Verify onboarding items such as identity checks.',
      'Approve or reject leave, and set leave entitlements.',
      'Open and close performance review cycles.',
    ],
    notYours: [
      'You do not activate accounts or invite Project Managers, Finance or Funders; the Admin does.',
      'You do not see project finance or approve invoices.',
    ],
    screens: ['People Ops', 'Onboarding', 'Consultants', 'Contracts', 'My Leave'],
  },
  {
    id: 'Funder',
    label: 'Funder',
    tagline: 'Approves funding and follows the money for the projects they support.',
    paused: true,
    firstSteps: [
      { text: 'Open the funding requests waiting for a decision.', href: '/funding/approval', link: 'Funding Approval' },
      { text: 'Look at the financial reports.', href: '/finance/reports', link: 'Financial Reports' },
    ],
    youDo: [
      'Approve funding requests.',
      'Follow the projects you fund and their financial reports.',
    ],
    notYours: [
      'You do not manage projects, people or payments.',
      'This role is paused: new accounts are not currently offered for it.',
    ],
    screens: ['Projects', 'Funding Approval', 'Financial Reports'],
  },
  {
    id: 'Partner',
    label: 'Partner (outside organisation)',
    tagline: 'An outside partner invited to work on one product, and nothing else.',
    firstSteps: [
      { text: 'Open the product you were given.', href: '/products', link: 'My Products' },
    ],
    youDo: [
      'Work on the data files of the one product you were invited to.',
    ],
    notYours: [
      'You see no ProTrack projects, finances, contracts or people.',
    ],
    screens: ['My Products'],
  },
]

// The extra-power labels the Admin sees when granting access (same wording as the server).
export const CAPABILITY_LABELS = {
  manage_users: 'Manage people and grant access',
  onboard_users: 'Onboard and invite users',
  manage_all_projects: 'Manage all projects',
  approve_invoices: 'Approve invoices',
  manage_finance: 'Finance dashboards and payments',
  close_projects: 'Close and archive projects',
  manage_system: 'System settings',
  view_people: 'View people and the HR dashboard',
  manage_onboarding: 'Manage onboarding and identity checks',
  manage_leave: 'Approve leave and set entitlements',
  manage_performance: 'Manage performance review cycles',
  restore_deleted: 'Restore deleted items (Recycle Bin)',
  act_on_behalf: 'Act for a consultant (edit their details, submit their work)',
  handle_tickets: 'Work on feedback and issues assigned to them',
}

// Three ways a person gets authority, and why "my menu looks different from theirs" is normal.
export const EXTRA_POWERS = [
  {
    title: 'Your role',
    text: 'Your main job in ProTrack. It decides which menus you see and what you can do by default.',
  },
  {
    title: 'Extra powers an Admin grants',
    text: 'An Admin can give one person a named power on top of their role: for example, to invite users, approve invoices, or see every project. You will then see the matching menu items.',
  },
  {
    title: 'Leading a project',
    text: 'The PM of a project, a person the PM lets manage it, or an approver named on it, can act on that project even if their role is different. A consultant who leads a sub-project manages it and is still a Consultant.',
  },
]

export const SPECIAL = [
  { title: 'Master PM', text: 'A Project Manager who sees and manages every project, not only their own.' },
  { title: 'Super Admin', text: 'Runs the platform itself: every product, the File Manager and the Platform panel. Holds every power an Admin has.' },
  { title: 'Organisation admin', text: 'Looks after one company\'s own settings, shown as "My Organisation". It is about that company, not about ProTrack\'s own roles.' },
]

// How long ProTrack keeps what, in plain words. Every line is checked against the code that does it
// (services/retention.py for the periods, routers/closure.py for the purge, services/personal_files.py
// for people's files). The first three periods are defaults a company can change; the people's-files
// period is one figure for everybody, because a person's account is shared across companies.
export const KEEPING = [
  { what: 'Finished work', period: '10 years', text: 'Deliverables, from the day the project is archived.' },
  { what: 'Working documents', period: '2 years', text: 'Documents the team worked on together, from the day they are archived.' },
  { what: 'Money records', period: '3 years', text: 'Contracts, invoices and bills, from the day the project is archived. The records and amounts stay; only the files go.' },
  { what: 'A consultant\'s own files', period: 'Kept', text: 'CV, identity documents, bank details and consents are kept, so a consultant can come back to work with us. An Admin erases them only if the person asks.' },
  { what: 'Chat and activity', period: 'As long as the project', text: 'Messages and the activity trail are locked with the project when it is archived and kept as long as the project itself. They are not deleted on a separate timer.' },
]

export const KEEPING_RULES = [
  'Nothing is deleted by itself. When a period ends, an Admin confirms the removal.',
  'A legal hold on a project stops every deletion in it, whatever the dates say.',
  'Records always stay: numbers, dates, amounts and who did what. Only the stored files go.',
  'When a project is archived every file is fingerprinted, so it can be shown later that nothing was altered. Every file that is destroyed is written to a permanent disposal record an Admin can download.',
]

export const GLOSSARY = [
  { term: 'Project', text: 'A piece of work with a budget, a team and milestones. A project can contain sub-projects.' },
  { term: 'Milestone', text: 'One part of a project, with a due date and an amount to be paid when it is finished.' },
  { term: 'Deliverable', text: 'The work a consultant submits for a milestone, for the PM to review.' },
  { term: 'Bill', text: 'A receipt for an expense a consultant wants repaid, attached to a deliverable.' },
  { term: 'Contract', text: 'The agreement with a consultant for the milestones they accepted. It needs two signatures.' },
  { term: 'Invoice', text: 'The request for payment raised when a milestone\'s deliverables are approved.' },
  { term: 'Dossier', text: 'A controlled bundle of documents that moves through checks and approvals, found in a project\'s Documents tab.' },
  { term: 'Onboarding', text: 'The steps a new consultant completes before working: profile, CV, personal details and consent.' },
]
